import { ArrowDownIcon, CalendarIcon } from '@radix-ui/react-icons'
import {
	type LinksFunction,
	type LoaderFunctionArgs,
	type MetaFunction,
	useLoaderData,
} from 'react-router'
import {
	FestivalFooter,
	FestivalGate,
	FestivalHero,
	FestivalStatus,
	FestivalWelcome,
	getSceneVenues,
	HelpCard,
	TicketInfo,
} from '#app/components/organisms/festival'
import { CalendarSubscribe } from '#app/components/molecules'
import { NextEventFab, ProgramSection } from '#app/components/organisms/program'
import { getPublicFestivalProgram } from '#app/domains/program'
import {
	formatFestivalSpan,
	getNextEvent,
	formatProgramHeadline,
} from '#app/domains/program/pure'
import { getEffectiveMode } from '#app/utils/maintenance.server.ts'
import { useNow } from '#app/utils/motion.ts'
import { getUserId } from '#app/utils/auth.server.ts'
import { getDomainUrl } from '#app/utils/misc.tsx'
import { pageMeta } from '#app/utils/page-meta.ts'
import { prisma } from '#app/utils/db.server.ts'
import {
	getBuyerCartOnlyEventIds,
	getBuyerEventIds,
} from '#app/domains/tickets'
import { isVippsConfigured } from '#app/domains/payments'
import { userHasPermission } from '#app/utils/permissions.server.ts'
import { useRevalidateOnFocus } from '#app/utils/use-revalidate-on-focus.ts'
import eventCardStyleUrl from '../../styles/event-card.css?url'
import festivalStyleUrl from '../../styles/festival.css?url'

export const meta: MetaFunction<typeof loader> = (args) =>
	pageMeta(args, { shareTitle: 'Huskonsertfestivalen på Vorrå' })

export const links: LinksFunction = () => [
	{ rel: 'stylesheet', href: festivalStyleUrl },
	{ rel: 'stylesheet', href: eventCardStyleUrl },
]

export async function loader({ request }: LoaderFunctionArgs) {
	// seat counts only — ticket rows (hash, holder name) never leave the server
	const program = await getPublicFestivalProgram()
	const festivalMode = getEffectiveMode(program?.festival)
	const isAdminPreview =
		festivalMode !== 'LIVE' &&
		(await userHasPermission(request, 'update:event:any'))
	const serverNow = Date.now()
	const origin = getDomainUrl(request)

	if (festivalMode !== 'LIVE' && !isAdminPreview) {
		return {
			events: [],
			myEventIds: [] as string[],
			cartEventIds: [] as string[],
			vippsOnline: isVippsConfigured(),
			origin,
			festival: null,
			serverNow,
			previewMode: festivalMode,
			gateReleaseAt: program?.festival.gateReleaseAt ?? null,
			gateMessage: program?.festival.gateMessage ?? null,
			isAdminPreview,
		}
	}

	// the visitor's own concerts: a badge on their cards and their own route;
	// those only in the cart yet say so
	const userId = await getUserId(request)
	const eventIds = program?.events.map((event) => event.id) ?? []
	const [myEventIds, cartEventIds] =
		userId && program
			? await Promise.all([
					getBuyerEventIds(prisma, userId, eventIds),
					getBuyerCartOnlyEventIds(prisma, userId, eventIds),
				])
			: [[], []]

	return {
		events: program?.events ?? [],
		myEventIds,
		cartEventIds,
		vippsOnline: isVippsConfigured(),
		origin,
		festival: program?.festival ?? null,
		serverNow,
		previewMode: festivalMode,
		gateReleaseAt: null,
		gateMessage: null,
		isAdminPreview,
	}
}

/**
 * Whether the gate/maintenance page shows instead of the festival site.
 * Admins (isAdminPreview) always see the full program, even in MAINTENANCE.
 */
export function isGateVisible(
	previewMode: string,
	isAdminPreview: boolean,
): boolean {
	return previewMode !== 'LIVE' && !isAdminPreview
}

const TITLE = 'Huskonsertfestivalen på Vorrå'
const TITLE_LINES = ['Huskonsert-', 'festivalen', 'på Vorrå']

export default function Index() {
	const {
		events,
		myEventIds,
		cartEventIds,
		vippsOnline,
		origin,
		festival,
		serverNow,
		previewMode,
		gateReleaseAt,
		gateMessage,
		isAdminPreview,
	} = useLoaderData<typeof loader>()
	const now = useNow(serverNow)
	// seats left stay current when a visitor comes back to the tab
	useRevalidateOnFocus()

	if (isGateVisible(previewMode, isAdminPreview)) {
		return (
			<FestivalGate
				mode={previewMode}
				message={gateMessage}
				releaseAt={gateReleaseAt}
				now={now}
			/>
		)
	}

	const adminPreview = previewMode !== 'LIVE' && isAdminPreview
	const first = events[0]
	const last = events.at(-1)
	const dates =
		first && last
			? formatFestivalSpan(first.date, last.date)
			: (festival?.datesText ?? null)
	const nextEvent = getNextEvent(events, now)

	return (
		<main className="hk">
			<FestivalHero
				notice={
					adminPreview
						? previewMode === 'MAINTENANCE'
							? 'Admin-førehandsvising: sida er under vedlikehald for publikum'
							: previewMode === 'COUNTDOWN'
								? 'Admin-førehandsvising: publikum ser ei nedteljing'
								: 'Admin-førehandsvising: festivalen er i planleggingsmodus for publikum'
						: null
				}
				eyebrow={`Vorrå, Karmøy${festival ? ` · ${festival.year}` : ''}`}
				title={TITLE}
				titleLines={TITLE_LINES}
				dates={dates}
				lede={
					<p>
						Ein festival for heile familien, arrangert av lokale musikantar.
					</p>
				}
				status={<FestivalStatus events={events} now={now} />}
				actions={
					<>
						<a className="hk-btn" href="#program">
							Sjå programmet
							<ArrowDownIcon aria-hidden="true" />
						</a>
						<a className="hk-btn hk-btn-ghost" href="#billetter">
							Billettar
						</a>
					</>
				}
				venues={getSceneVenues(events)}
			/>

			<FestivalWelcome
				introText={festival?.introText ?? null}
				events={events}
			/>

			<section
				id="program"
				className="hk-section"
				aria-labelledby="program-title"
			>
				<div className="hk-container">
					<header className="hk-section-head">
						<p className="hk-kicker">Program</p>
						<h2 id="program-title" className="hk-h2">
							{formatProgramHeadline(events)}
						</h2>
						<p className="hk-section-lede">
							Konsertane blir haldne i private heimar og naust på Vorrå. Trykk
							på ein konsert for meir om han og for å reservere billettar.
						</p>
						{events.length ? (
							<div className="hk-cal">
								<p className="hk-cal-title">
									<CalendarIcon aria-hidden="true" /> Heile programmet i
									kalenderen — oppdaterer seg sjølv
								</p>
								<CalendarSubscribe feedUrl={`${origin}/program.ics`} />
							</div>
						) : null}
					</header>
					<ProgramSection
						events={events}
						now={now}
						myEventIds={myEventIds}
						cartEventIds={cartEventIds}
					/>
				</div>
			</section>

			<TicketInfo
				festival={festival}
				events={events}
				vippsOnline={vippsOnline}
			/>

			<HelpCard
				name={festival?.contactName ?? 'Arne Skorpe Sjøen'}
				phone={festival?.contactPhone ?? '41104852'}
				email={festival?.contactEmail ?? 'sjoen90@gmail.com'}
			/>

			<FestivalFooter />
			<NextEventFab nextEventId={nextEvent?.id ?? null} hideWhile=".hk-hero" />
		</main>
	)
}
